import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  fetchPublicScoringGames,
  setRoundFilter,
  setGreenRinkFilter,
  clearFilters
} from '../../features/publicScoring/publicScoringSlice';
import { publicStartGame } from '../../features/game/gameSlice';
import { useColorMode } from '../../hooks/colorModeProvider';
import { GameStatus, getGameStatus } from '../../constants/gameConstants';

const DEFAULT_FILTERS = {
  round: null,
  greenRink: null
};

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const getTeamDisplayName = (team, fallback) => {
  if (!team) return fallback;
  if (typeof team === 'string') return team;
  if (team.name) return team.name;
  if (Array.isArray(team.players) && team.players.length > 0) {
    return team.players.join(' / ');
  }
  if (team.id) return team.id;
  return fallback;
};

const getTeamColor = (team) => {
  if (!team || typeof team === 'string') return null;

  const groupColor = team.groupColor || team.group?.color;
  if (HEX_COLOR_PATTERN.test(groupColor || '')) {
    return groupColor;
  }

  if (HEX_COLOR_PATTERN.test(team.color || '')) {
    return team.color;
  }

  return team.color ? `${team.color}.main` : null;
};

const getPublicActionLabel = (status) => {
  switch (status) {
    case GameStatus.NOT_STARTED:
      return 'Start Game';
    case GameStatus.IN_PROGRESS:
    case GameStatus.PLAYOFF:
      return 'Score Game';
    case GameStatus.COMPLETE:
      return 'View';
    default:
      return 'Open';
  }
};

const PublicScoringAllGames = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [startedGameIds, setStartedGameIds] = useState({});
  const [startingGameIds, setStartingGameIds] = useState({});

  const greenFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const green = searchParams.get('green');
    if (!green) {
      return null;
    }

    const trimmedGreen = green.trim();
    return trimmedGreen ? trimmedGreen.toUpperCase() : null;
  }, [location.search]);

  const requestKey = `${eventId || 'missing'}::${greenFromQuery || 'all'}`;

  const { mode, setMode } = useColorMode();
  const games = useSelector((state) => state.publicScoring.gamesByEventId[requestKey] || []);
  const loading = useSelector((state) => state.publicScoring.loadingByEventId[requestKey] ?? false);
  const error = useSelector((state) => state.publicScoring.errorByEventId[requestKey] || null);
  const eventName = useSelector((state) => state.publicScoring.eventNamesById[eventId] || '');
  const sectionNames = useSelector(
    (state) => state.publicScoring.sectionNamesByEventId[eventId] || {}
  );
  const eventFiltersByEventId = useSelector(
    (state) => state.publicScoring.filtersByEventId[eventId]
  );
  const eventFilters = eventFiltersByEventId || DEFAULT_FILTERS;

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  // Initialize filters for this event if they don't exist
  useEffect(() => {
    if (eventId && !eventFiltersByEventId) {
      dispatch(setRoundFilter({ eventId, round: null }));
      dispatch(setGreenRinkFilter({ eventId, greenRink: null }));
    }
  }, [eventId, eventFiltersByEventId, dispatch]);

  // Always re-read on mount: a score or photo saved on another page must not
  // leave this list showing the status it had before that write.
  useEffect(() => {
    if (!eventId) {
      return;
    }

    dispatch(fetchPublicScoringGames({ eventId, green: greenFromQuery, force: true }));
  }, [eventId, greenFromQuery, dispatch]);

  useEffect(() => {
    if (!eventId) {
      return undefined;
    }

    const refresh = () => {
      dispatch(fetchPublicScoringGames({ eventId, green: greenFromQuery, force: true }));
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [eventId, greenFromQuery, dispatch]);

  const rounds = useMemo(
    () => [...new Set(games.map((g) => g.round))].sort((a, b) => a - b),
    [games]
  );

  const greenRinks = useMemo(
    () => [...new Set(games.map((g) => `${g.green}${g.rink}`))].sort(),
    [games]
  );

  const filteredAndSortedGames = useMemo(() => {
    let filteredGames = games;

    if (eventFilters.round !== null) {
      filteredGames = filteredGames.filter((game) => game.round === eventFilters.round);
    }

    if (eventFilters.greenRink !== null) {
      const match = eventFilters.greenRink.match(/^([A-Z]+)(\d+)$/);
      if (match) {
        const [, green, rink] = match;
        filteredGames = filteredGames.filter(
          (game) => game.green === green && game.rink === parseInt(rink, 10)
        );
      }
    }

    const statusPriority = {
      [GameStatus.IN_PROGRESS]: 0,
      [GameStatus.NOT_STARTED]: 1,
      [GameStatus.COMPLETE]: 2,
      [GameStatus.PLAYOFF]: 1.5
    };

    return [...filteredGames].sort((a, b) => {
      const aPriority = statusPriority[a.status] ?? 999;
      const bPriority = statusPriority[b.status] ?? 999;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      if (a.round !== b.round) {
        return a.round - b.round;
      }

      const aGreenRink = `${a.green}${a.rink}`;
      const bGreenRink = `${b.green}${b.rink}`;
      if (aGreenRink !== bGreenRink) {
        return aGreenRink.localeCompare(bGreenRink);
      }

      return 0;
    });
  }, [games, eventFilters.round, eventFilters.greenRink]);

  if (!eventId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          Missing event ID
        </Typography>
      </Box>
    );
  }

  const handleRoundFilterChange = (e) => {
    dispatch(setRoundFilter({ eventId, round: e.target.value === '' ? null : e.target.value }));
  };

  const handleGreenRinkFilterChange = (e) => {
    dispatch(
      setGreenRinkFilter({ eventId, greenRink: e.target.value === '' ? null : e.target.value })
    );
  };

  const handleClearFilters = () => {
    dispatch(clearFilters({ eventId }));
  };

  const handleRefreshGames = () => {
    dispatch(fetchPublicScoringGames({ eventId, green: greenFromQuery, force: true }));
  };

  const isGameStartedForAction = (game) => {
    if (game.status !== GameStatus.NOT_STARTED) {
      return true;
    }

    return !!startedGameIds[game.id];
  };

  const getRowActionLabel = (game) => {
    if (game.status === GameStatus.NOT_STARTED && startedGameIds[game.id]) {
      return 'Score Game';
    }

    return getPublicActionLabel(game.status);
  };

  const handleGameAction = async (game) => {
    if (!isGameStartedForAction(game)) {
      setStartingGameIds((prev) => ({ ...prev, [game.id]: true }));

      try {
        await dispatch(publicStartGame({ gameId: game.id })).unwrap();
        setStartedGameIds((prev) => ({ ...prev, [game.id]: true }));
      } catch (error) {
        // keep button as Start Game when start fails
      } finally {
        setStartingGameIds((prev) => ({ ...prev, [game.id]: false }));
      }
      return;
    }

    navigate(`/public/scoring-game/${game.id}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading games...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{ p: 2 }}>
      <Box sx={{ alignSelf: 'flex-start', mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/public-scoring/events')}>
          ← Back to Events
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        {eventName} - All Games
      </Typography>

      {/* Filters */}
      <Stack
        direction="row"
        gap={2}
        sx={{ mb: 3, width: '100%', maxWidth: 600, justifyContent: 'center', flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Round</InputLabel>
          <Select value={eventFilters.round ?? ''} onChange={handleRoundFilterChange} label="Round">
            <MenuItem value="">All Rounds</MenuItem>
            {rounds.map((round) => (
              <MenuItem key={round} value={round}>
                Round {round}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Green/Rink</InputLabel>
          <Select
            value={eventFilters.greenRink ?? ''}
            onChange={handleGreenRinkFilterChange}
            label="Green/Rink">
            <MenuItem value="">All Green/Rinks</MenuItem>
            {greenRinks.map((gr) => (
              <MenuItem key={gr} value={gr}>
                {gr}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={handleClearFilters} sx={{ height: 56 }}>
          Clear Filters
        </Button>
      </Stack>

      {filteredAndSortedGames.length === 0 ? (
        <Typography variant="h6">No games found</Typography>
      ) : (
        <Stack alignItems="stretch" gap={1} sx={{ width: '100%', maxWidth: 900 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              mb: 1
            }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
              Games ({filteredAndSortedGames.length} game
              {filteredAndSortedGames.length !== 1 ? 's' : ''})
            </Typography>
            <Button variant="contained" color="info" size="small" onClick={handleRefreshGames}>
              Refresh
            </Button>
          </Box>

          {filteredAndSortedGames.map((game) => (
            <Box
              key={game.id}
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
              <Box>
                <Typography variant="body1">
                  <Box
                    component="span"
                    sx={{
                      color: getTeamColor(game?.teams?.teamA) || 'text.primary',
                      fontWeight: 600
                    }}>
                    {getTeamDisplayName(game?.teams?.teamA, 'Team A')}
                  </Box>
                  {' vs '}
                  <Box
                    component="span"
                    sx={{
                      color: getTeamColor(game?.teams?.teamB) || 'text.primary',
                      fontWeight: 600
                    }}>
                    {getTeamDisplayName(game?.teams?.teamB, 'Team B')}
                  </Box>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {sectionNames[game.section] || game.sectionName || '-'} • Round{' '}
                  {game.round ?? '-'} • {game.green ?? '-'}
                  {game.rink ?? '-'} • {getGameStatus(game.status)}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                disabled={!!startingGameIds[game.id]}
                onClick={() => handleGameAction(game)}>
                {startingGameIds[game.id] ? 'Starting...' : getRowActionLabel(game)}
              </Button>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default PublicScoringAllGames;
